import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getStorageKeyForLines } from '../utils';
import logger from '../../../../../utils/logger';

export const usePriceLines = (selectedInstrument, candleSeriesRef) => {
    const [priceLines, setPriceLines] = useState(() => {
        if (!selectedInstrument) return [];
        
        try {
            const storageKey = getStorageKeyForLines(selectedInstrument);
            if (!storageKey) return [];
            
            const savedLines = localStorage.getItem(storageKey);
            if (savedLines) {
                return JSON.parse(savedLines).map(line => ({
                    ...line,
                    ref: null // We'll create the refs when chart is ready
                }));
            }
        } catch (error) {
            logger.warn('Error loading saved lines:', error);
        }
        return [];
    });

    const createPriceLine = useCallback((price, type = 'custom', text = '') => {
        if (!candleSeriesRef.current || !selectedInstrument) return;

        const lineStyle = {
            color: type === 'support' ? 'rgba(38, 166, 154, 0.6)' : 
                   type === 'resistance' ? 'rgba(239, 83, 80, 0.6)' : 
                   'rgba(33, 150, 243, 0.6)',
            lineWidth: type === 'custom' ? 1 : 2,
            lineStyle: 0,
            title: text || (type === 'support' ? 'Support' : type === 'resistance' ? 'Resistance' : 'Custom')
        };

        const priceLine = candleSeriesRef.current.createPriceLine({
            price,
            color: lineStyle.color,
            lineWidth: lineStyle.lineWidth,
            lineStyle: lineStyle.lineStyle,
            axisLabelVisible: true,
            title: lineStyle.title
        });

        const newLine = {
            id: uuidv4(),
            price,
            type,
            text: lineStyle.title,
            ref: priceLine,
            instrument: selectedInstrument
        };

        setPriceLines(prev => [...prev, newLine]);
    }, [selectedInstrument, candleSeriesRef]);

    const removePriceLine = useCallback((lineId) => {
        if (!selectedInstrument) return;
        
        setPriceLines(prev => {
            const line = prev.find(l => l.id === lineId);
            if (!line) return prev;

            if (line.ref && candleSeriesRef.current) {
                candleSeriesRef.current.removePriceLine(line.ref);
            }

            const newLines = prev.filter(l => l.id !== lineId);
            
            try {
                const storageKey = getStorageKeyForLines(selectedInstrument);
                if (storageKey) {
                    const linesToSave = newLines
                        .filter(l => !l.isProvided)
                        .map(({ id, price, type, text }) => ({
                            id, price, type, text
                        }));
                        
                    localStorage.setItem(storageKey, JSON.stringify(linesToSave));
                }
            } catch (error) {
                logger.warn('Error saving lines after removal:', error);
            }

            return newLines;
        });
    }, [selectedInstrument, candleSeriesRef]);

    const deleteAllLines = useCallback(() => {
        if (!selectedInstrument) return;
        
        const linesToRemove = [...priceLines];
        linesToRemove.forEach((line, index) => {
            setTimeout(() => {
                try {
                    removePriceLine(line.id);
                } catch (error) {
                    logger.warn('Error removing price line:', error);
                }
            }, index * 100);
        });
    }, [selectedInstrument, priceLines, removePriceLine]);

    useEffect(() => {
        const saveLines = () => {
            if (!selectedInstrument) return;
            
            try {
                const storageKey = getStorageKeyForLines(selectedInstrument);
                if (!storageKey) return;
                
                const linesToSave = priceLines
                    .filter(line => !line.isProvided && line.instrument === selectedInstrument)
                    .map(({ id, price, type, text }) => ({
                        id, price, type, text
                    }));
                    
                localStorage.setItem(storageKey, JSON.stringify(linesToSave));
            } catch (error) {
                logger.warn('Error saving lines:', error);
            }
        };

        const timeoutId = setTimeout(saveLines, 300);
        return () => clearTimeout(timeoutId);
    }, [priceLines, selectedInstrument]);

    return {
        priceLines,
        createPriceLine,
        removePriceLine,
        deleteAllLines
    };
}; 